/**
 * TC-DSL Generator
 *
 * Converts a Strategy tree to TradeCraft DSL text.
 * Reference: docs/TradeCraft_DSL.md
 */

import type { Strategy, StrategyNode, Signal, FilterSignal, Condition } from '../types/builder';
import { SIGNAL_DSL_NAMES } from '../types/builder';

// ─── Signal Formatting ──────────────────────────────────────

function formatSignal(signal: Signal): string {
  const method = SIGNAL_DSL_NAMES[signal.type];
  if (signal.type === 'current-price') return `${signal.ticker}.${method}`;
  return `${signal.ticker}.${method}(${signal.window ?? ''})`;
}

function formatFilterSignal(signal: FilterSignal): string {
  const method = SIGNAL_DSL_NAMES[signal.type];
  if (signal.type === 'current-price') return method;
  return `${method}(${signal.window ?? ''})`;
}

function formatCondition(condition: Condition): string {
  const left = formatSignal(condition.left);
  const right = typeof condition.right === 'number'
    ? condition.right.toString()
    : formatSignal(condition.right);
  return `${left} ${condition.operator} ${right}`;
}

// ─── Node → DSL Lines ───────────────────────────────────────

function nodeToLines(node: StrategyNode, indent: number): string[] {
  const prefix = '  '.repeat(indent);
  const lines: string[] = [];

  switch (node.type) {
    case 'asset': {
      const base = node.ticker || '???';
      if (node.name) {
        lines.push(`${prefix}${base} as "${node.name}"`);
      } else {
        lines.push(`${prefix}${base}`);
      }
      break;
    }

    case 'group': {
      lines.push(`${prefix}group "${node.name}":`);
      lines.push(...nodeToLines(node.child, indent + 1));
      break;
    }

    case 'weight-equal': {
      lines.push(`${prefix}equal:`);
      for (const child of node.children) {
        lines.push(...childToListItem(child, indent + 1));
      }
      break;
    }

    case 'weight-specified': {
      lines.push(`${prefix}weights:`);
      for (let i = 0; i < node.children.length; i++) {
        const pct = Math.round((node.weights[i] ?? 0) * 100);
        const child = node.children[i];
        lines.push(`${prefix}  - ${pct}%:`);
        lines.push(...nodeToLines(child, indent + 2));
      }
      break;
    }

    case 'weight-inverse-volatility': {
      lines.push(`${prefix}inv_vol(${node.window}):`);
      for (const child of node.children) {
        lines.push(...childToListItem(child, indent + 1));
      }
      break;
    }

    case 'if': {
      lines.push(`${prefix}if ${formatCondition(node.condition)}:`);
      lines.push(...nodeToLines(node.thenBranch, indent + 1));
      lines.push(`${prefix}else:`);
      lines.push(...nodeToLines(node.elseBranch, indent + 1));
      break;
    }

    case 'filter': {
      const dir = node.selector.direction;
      const count = node.selector.count;
      const sig = formatFilterSignal(node.signal);
      lines.push(`${prefix}filter ${dir}(${count}) by ${sig}:`);
      for (const child of node.children) {
        lines.push(...childToListItem(child, indent + 1));
      }
      break;
    }
  }

  return lines;
}

/** Render child as an indented list item: -\n  child */
function childToListItem(child: StrategyNode, indent: number): string[] {
  const prefix = '  '.repeat(indent);
  // Always use indented format: dash on its own line, child indented below
  return [`${prefix}-`, ...nodeToLines(child, indent + 1)];
}

// ─── Public API ─────────────────────────────────────────────

/** Generate a complete TC-DSL string from a Strategy */
export function generateDSL(strategy: Strategy): string {
  const lines: string[] = [];
  const meta = strategy.metadata;

  lines.push(`strategy "${meta.name}":`);
  lines.push('  asset_class: EQUITIES');
  if (meta.rebalanceMode === 'threshold' && meta.rebalanceThreshold) {
    lines.push(`  rebalance: threshold(${meta.rebalanceThreshold})`);
  } else {
    lines.push('  rebalance: daily');
  }
  if (meta.description) {
    lines.push(`  description: "${meta.description}"`);
  }
  lines.push('');
  lines.push('  root:');

  lines.push(...nodeToLines(strategy.root, 2));

  return lines.join('\n');
}
